import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Skeleton, Switch, Tooltip, Space, Typography } from 'antd';
const { Text } = Typography;
import { searchAgents } from '@/api/agents';
import {
  RobotOutlined,
  ToolOutlined,
  RightOutlined,
  HeartOutlined,
  CheckSquareOutlined,
  DollarCircleOutlined,
  CodeOutlined,
  WarningOutlined,
  ApiOutlined,
  AreaChartOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { spendCoverage } from '@/api/insights';


// Polished dashboard. Hero header + glanceable KPIs + quick action shortcuts +
// recent activity placeholder. Replaces the original placeholder card grid.

export const DashboardPlaceholder = observer(function DashboardPlaceholder() {
  const {
    merchantContextStore: ctx,
    insightsStore: insights,
  } = useStore();

  useEffect(() => {
    if (ctx.merchants.length === 0 && !ctx.isLoading) void ctx.loadMerchants();
  }, [ctx]);

  // Insights auto-fetch on merchant change via reaction; trigger if needed
  // when the dashboard mounts on an already-selected merchant.
  useEffect(() => {
    if (
      ctx.selectedMerchantId != null &&
      insights.tokenCost.length === 0 &&
      insights.contextOverload.length === 0 &&
      !insights.loading.tokenCost
    ) {
      void insights.fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  const merchant = ctx.selectedMerchant;

  // Effective agent count for the "Agents in this merchant" tile.
  // We can't reuse agentRegistryStore.totalCount here because that store
  // defaults to includeGlobal=false (the Agents page lets the user toggle
  // it; the dashboard should always show what's actually USABLE in this
  // merchant, which includes global agents). A small dedicated fetch
  // keeps the dashboard honest without changing the Agents page filter
  // behavior. Page size 1 — we only need paging.totalRecordCount.
  const [effectiveAgentCount, setEffectiveAgentCount] = useState<number | null>(null);
  useEffect(() => {
    const merchantId = ctx.selectedMerchantId;
    if (merchantId == null) {
      setEffectiveAgentCount(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await searchAgents({
          merchantId,
          pageNo: 1,
          pageSize: 1,
          includeGlobal: true,
        });
        if (cancelled) return;
        setEffectiveAgentCount(res.paging?.totalRecordCount ?? res.data?.length ?? 0);
      } catch {
        if (!cancelled) setEffectiveAgentCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.selectedMerchantId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 32 }}>
      {/* Hero greeting block removed 2026-05-01 — was taking prime real
          estate above the fold without surfacing actionable information.
          The KPI row + Top pressures are the genuine signal; let them
          breathe. Greeting / first-name / "Currently viewing X" can come
          back later as a single subtle line if we miss the orientation. */}

      {/* KPI Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
        }}
      >
        <KpiTile
          accent="violet"
          label="Agents in this merchant"
          value={effectiveAgentCount ?? '—'}
          hint={
            !merchant
              ? 'Switch merchant to load'
              : effectiveAgentCount == null
                ? 'Loading…'
                : effectiveAgentCount > 0
                  ? 'Click to view the roster'
                  : 'No agents available — onboard one in the Agents page'
          }
          icon={<RobotOutlined />}
          onClick={merchant ? () => (window.location.href = '/agents') : undefined}
        />
        <KpiTile
          accent="green"
          label="Health checks (today)"
          value="—"
          hint="Coming in Sprint 3"
          icon={<CheckSquareOutlined />}
        />
        <SpendKpi />
      </div>

      {/* Top pressures — agents to keep an eye on */}
      <TopPressures />

      {/* Two-column: Quick actions + What's live */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 16 }}>
        <SectionCard
          title="Quick actions"
          subtitle="Jump straight into the most common workflows."
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <QuickAction
              to="/agents"
              icon={<RobotOutlined />}
              title="Browse agents"
              desc="Roster + edit configurations"
              accent="ink"
            />
            <QuickAction
              to="/skills"
              icon={<ToolOutlined />}
              title="Skills library"
              desc="Reusable capabilities agents load on demand"
              accent="violet"
            />
            <QuickAction
              to="/agents"
              icon={<HeartOutlined />}
              title="Run health checks"
              desc="Validate agent accuracy"
              accent="green"
              disabled
              note="Soon"
            />
            <QuickAction
              to="/models"
              icon={<CodeOutlined />}
              title="Models catalog"
              desc="Pricing + deployment"
              accent="amber"
              disabled
              note="Soon"
            />
          </div>
        </SectionCard>

        <SectionCard title="Recent activity" subtitle="Last few changes across this merchant">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ActivityRow tone="green" text="Real-time data wired" detail="Agents and skills now read from MemberCare DB" />
            <ActivityRow tone="ink" text="Cmd+K command palette" detail="Search agents, merchants, commands" />
            <ActivityRow tone="violet" text="Skills" detail="List + detail surfaces shipped" />
            <ActivityRow tone="amber" text="Health checks UI" detail="Coming next sprint" muted />
          </div>
        </SectionCard>
      </div>
    </div>
  );
});

// ── Live LLM spend KPI ─────────────────────────────────────────────────────

const SpendKpi = observer(function SpendKpi() {
  const { insightsStore: insights } = useStore();
  const days = insights.days;
  const spend = spendCoverage(insights.tokenCost);
  const isLoading = insights.loading.tokenCost && insights.tokenCost.length === 0;

  return (
    <KpiTile
      accent="amber"
      label={`LLM spend (${days}d)`}
      // A call without a cost is never priced at $0: when any are missing, the figure is a floor and says so.
      value={isLoading ? '…' : `${spend.missing > 0 ? '≥ ' : ''}$${spend.usd.toFixed(2)}`}
      hint={
        isLoading
          ? 'Loading'
          : spend.missing > 0
            ? `${spend.missing.toLocaleString()} of ${spend.calls.toLocaleString()} calls have no cost — real spend is higher`
            : `${spend.calls.toLocaleString()} LLM calls in window`
      }
      icon={<DollarCircleOutlined />}
      onClick={() => (window.location.href = '/insights')}
    />
  );
});

// ── Top pressures — ranked lists ───────────────────────────────────────────
//
// Two side-by-side cards on the dashboard. Surfaces agents that are most at
// risk of context overflow (high avg PercentOfContext) and agents producing
// the largest replies (high avg output tokens / call).

const TopPressures = observer(function TopPressures() {
  const { insightsStore: insights, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const days = insights.days;

  // Recency filter — defaults to true so operators see ACTIVE pressure,
  // not stale aggregations. The 4-days-ago Opus test calls that floated
  // to the top of the ranking on 2026-04-28 are exactly the case this
  // filter is for: mathematically the highest avg ctx %, but operationally
  // dead. Toggle off to widen back to the full window.
  const [recentOnly, setRecentOnly] = useState(true);
  // Recompute the cutoff each render — cheap, and the recency filter
  // is meant to track wall-clock time, not the merchant or window state.
  const recencyCutoff = Date.now() - 24 * 60 * 60 * 1000;

  function isRecent(lastSeenIso: string | null | undefined): boolean {
    if (!lastSeenIso) return false;
    const t = new Date(lastSeenIso).getTime();
    return Number.isFinite(t) && t >= recencyCutoff;
  }

  // Top 5 by avg context %
  const topContext = useMemo(() => {
    return [...insights.contextOverload]
      .filter((b) => b.avgPercentOfContext != null)
      .filter((b) => !recentOnly || isRecent(b.lastSeenUtc))
      .sort((a, b) => Number(b.avgPercentOfContext ?? 0) - Number(a.avgPercentOfContext ?? 0))
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insights.contextOverload, recentOnly]);

  // Top 5 by avg output tokens per call
  const topOutput = useMemo(() => {
    return [...insights.tokenCost]
      .filter((b) => b.callCount > 0)
      .filter((b) => !recentOnly || isRecent(b.lastSeenUtc))
      .map((b) => ({ ...b, _avgOut: b.outputTokens / Math.max(1, b.callCount) }))
      .sort((a, b) => b._avgOut - a._avgOut)
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insights.tokenCost, recentOnly]);

  const isLoading =
    (insights.loading.contextOverload && insights.contextOverload.length === 0) ||
    (insights.loading.tokenCost && insights.tokenCost.length === 0);

  return (
    <SectionCard
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AreaChartOutlined /> Top pressures
        </span>
      }
      subtitle={
        recentOnly
          ? 'Agents with calls in the last 24h. Click any row to drill into the full Insights surface.'
          : `Agents to watch over the last ${days} days. Showing all rows in window — including stale buckets. Click any row to dig in.`
      }
      actions={
        <Space size={10}>
          <Tooltip title="Recent only filters out (agent × model) buckets with no calls in the last 24h. Useful when stale test runs (e.g., a one-off Opus benchmark) float to the top of the ranking.">
            <Space size={6}>
              <Switch size="small" checked={recentOnly} onChange={setRecentOnly} />
              <Text style={{ fontSize: 12 }}>Recent only (24h)</Text>
            </Space>
          </Tooltip>
          <Link to="/insights" style={{ fontSize: 12 }}>
            See all insights →
          </Link>
        </Space>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <PressureColumn
          title="Highest avg context %"
          icon={<WarningOutlined />}
          tone="amber"
          isDark={isDark}
          isLoading={isLoading}
          empty={
            insights.contextOverload.length === 0
              ? 'No prompt logs in window'
              : 'No agents with measured context %'
          }
        >
          {topContext.map((b, i) => (
            <PressureRow
              key={`${b.agentId ?? 'na'}-${b.modelName ?? 'na'}-${i}`}
              rank={i + 1}
              label={b.agentName ?? '(unattributed)'}
              sublabel={b.modelName ?? '—'}
              value={`${Number(b.avgPercentOfContext ?? 0).toFixed(1)}%`}
              valueTone={
                Number(b.avgPercentOfContext ?? 0) >= 80
                  ? 'red'
                  : Number(b.avgPercentOfContext ?? 0) >= 50
                  ? 'amber'
                  : 'ink'
              }
              barPct={Math.min(100, Number(b.avgPercentOfContext ?? 0))}
              isDark={isDark}
              agentId={b.agentId ?? null}
              drillAnchor="thread-context"
              callCount={b.callCount}
              lastSeenUtc={b.lastSeenUtc}
              modelIsActive={b.modelIsActive}
            />
          ))}
        </PressureColumn>

        <PressureColumn
          title="Highest avg output / call"
          icon={<ApiOutlined />}
          tone="violet"
          isDark={isDark}
          isLoading={isLoading}
          empty={
            insights.tokenCost.length === 0
              ? 'No prompt logs in window'
              : 'No agents with measured output'
          }
        >
          {topOutput.map((b, i) => {
            const max = topOutput[0]?._avgOut ?? 1;
            const pct = (b._avgOut / max) * 100;
            return (
              <PressureRow
                key={`${b.agentId ?? 'na'}-${b.modelName ?? 'na'}-${i}`}
                rank={i + 1}
                label={b.agentName ?? '(unattributed)'}
                sublabel={b.modelName ?? '—'}
                value={`${Math.round(b._avgOut).toLocaleString()} tok`}
                valueTone="violet"
                barPct={pct}
                isDark={isDark}
                agentId={b.agentId ?? null}
                drillAnchor="tool-overflow"
                callCount={b.callCount}
                lastSeenUtc={b.lastSeenUtc}
                modelIsActive={b.modelIsActive}
              />
            );
          })}
        </PressureColumn>
      </div>
    </SectionCard>
  );
});

function PressureColumn({
  title,
  icon,
  tone,
  isDark,
  isLoading,
  empty,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  tone: 'amber' | 'violet';
  isDark: boolean;
  isLoading: boolean;
  empty: string;
  children: React.ReactNode;
}) {
  const fg = tone === 'amber' ? colors.amber[600] : colors.violet[600];
  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '10px 4px 12px',
      }}
    >
      <div
        style={{
          padding: '4px 14px 10px',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: fg,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {icon}
        {title}
      </div>
      {isLoading ? (
        <div style={{ padding: '24px 14px' }}>
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </div>
      ) : Array.isArray(children) && (children as unknown[]).length === 0 ? (
        <div
          style={{
            padding: '20px 14px',
            fontSize: 12.5,
            color: isDark ? colors.slate[400] : colors.slate[500],
            fontStyle: 'italic',
          }}
        >
          {empty}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
      )}
    </div>
  );
}

function PressureRow({
  rank,
  label,
  sublabel,
  value,
  valueTone,
  barPct,
  isDark,
  /** Optional agent id; when set, the row becomes a click-through to
      /insights?agentId=N which scopes the Tool overflow tile to that
      agent — answers "what plugins on THIS agent are blowing up output
      tokens?" in 1-2 clicks. */
  agentId,
  /** Anchor on the Insights page that the click should scroll into view. */
  drillAnchor,
  /** Total call count in this bucket. Shown inline so operators can spot
      "0.5% avg ctx but only 3 calls" — i.e., not real pressure. */
  callCount,
  /** ISO timestamp of the most recent call. Shown as a relative-time
      string ("4 days ago") so stale buckets are immediately visible. */
  lastSeenUtc,
  /** Whether the underlying ai.AIModel row is Active=1. False/null fades
      the row + adds a tooltip explaining the model isn't routable. */
  modelIsActive,
}: {
  rank: number;
  label: string;
  sublabel: string;
  value: string;
  valueTone: 'red' | 'amber' | 'violet' | 'ink';
  barPct: number;
  isDark: boolean;
  agentId?: number | null;
  drillAnchor?: string;
  callCount?: number;
  lastSeenUtc?: string | null;
  modelIsActive?: boolean | null;
}) {
  const fg =
    valueTone === 'red'
      ? colors.red[600]
      : valueTone === 'amber'
      ? colors.amber[600]
      : valueTone === 'violet'
      ? colors.violet[600]
      : colors.ink[600];
  // Build the drill URL once. agentId=null/undefined keeps the row
  // visually identical but non-clickable (no cursor change, no underline).
  const drillTo = agentId
    ? `/insights?agentId=${agentId}${drillAnchor ? `#${drillAnchor}` : ''}`
    : null;
  // Inactive models = ai.AIModel.Active = 0. Their pressure numbers are
  // historical only because the runtime won't route to them. Fade the
  // row so operators don't mistake stale aggregations for live signal.
  const inactive = modelIsActive === false;
  // Relative time helper — the dashboard is the only place we use this,
  // so a tiny implementation beats pulling in a date library here.
  const lastSeenLabel = (() => {
    if (!lastSeenUtc) return null;
    const ms = Date.now() - new Date(lastSeenUtc).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const m = Math.round(ms / 60_000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  })();
  const content = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '24px 1fr 110px',
        gap: 10,
        alignItems: 'center',
        padding: '8px 14px',
        borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
        cursor: drillTo ? 'pointer' : 'default',
        transition: 'background-color 100ms ease',
        opacity: inactive ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        if (drillTo) e.currentTarget.style.background = isDark ? colors.slate[800] : colors.slate[50];
      }}
      onMouseLeave={(e) => {
        if (drillTo) e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
          fontWeight: 700,
          color: isDark ? colors.slate[500] : colors.slate[400],
        }}
      >
        {rank}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: isDark ? colors.slate[100] : colors.slate[900],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 10.5,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[400] : colors.slate[500],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 1,
          }}
        >
          {sublabel}
          {callCount != null && (
            <span style={{ marginLeft: 6 }}>
              · {callCount.toLocaleString()} call{callCount === 1 ? '' : 's'}
            </span>
          )}
          {lastSeenLabel && <span style={{ marginLeft: 6 }}>· {lastSeenLabel}</span>}
          {inactive && (
            <Tooltip title="The corresponding ai.AIModel row has Active=0. The runtime won't route new traffic to this model, so these numbers are historical only — usually a one-off test or a deprecated deployment.">
              <span
                style={{
                  marginLeft: 8,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: isDark ? colors.slate[700] : colors.slate[200],
                  color: isDark ? colors.slate[300] : colors.slate[600],
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                inactive
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
            color: fg,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        <span
          style={{
            display: 'inline-block',
            width: 80,
            height: 4,
            borderRadius: 2,
            background: isDark ? colors.slate[700] : colors.slate[100],
            overflow: 'hidden',
            position: 'relative',
          }}
          aria-hidden
        >
          <span
            style={{
              position: 'absolute',
              inset: 0,
              width: `${barPct}%`,
              background: fg,
            }}
          />
        </span>
      </div>
    </div>
  );
  // When the row has an agent id, wrap in a router Link. text-decoration:
  // none keeps the row's visual styling identical so the only signal of
  // interactivity is the cursor + hover background — operators don't get
  // an unexpected blue underline.
  return drillTo ? (
    <Link to={drillTo} style={{ color: 'inherit', textDecoration: 'none' }}>
      {content}
    </Link>
  ) : (
    content
  );
}

const QuickAction = observer(function QuickAction({
  to,
  icon,
  title,
  desc,
  accent,
  disabled,
  note,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  accent: 'ink' | 'violet' | 'green' | 'amber';
  disabled?: boolean;
  note?: string;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const accentColor = {
    ink: isDark ? colors.ink[100] : colors.ink[600],
    violet: isDark ? colors.violet[100] : colors.violet[600],
    green: isDark ? colors.green[100] : colors.green[600],
    amber: isDark ? colors.amber[100] : colors.amber[600],
  }[accent];

  const accentBg = {
    ink: isDark ? colors.ink[700] + '40' : colors.ink[50],
    violet: isDark ? colors.violet[700] + '40' : colors.violet[50],
    green: isDark ? colors.green[700] + '40' : colors.green[100],
    amber: isDark ? colors.amber[700] + '40' : colors.amber[100],
  }[accent];

  const cardBg = isDark ? colors.slate[800] : '#FFFFFF';
  const cardBorder = isDark ? colors.slate[700] : colors.slate[200];
  const titleColor = isDark ? colors.slate[50] : colors.slate[900];
  const descColor = isDark ? colors.slate[400] : colors.slate[500];
  const noteBg = isDark ? colors.slate[700] : colors.slate[100];
  const noteColor = isDark ? colors.slate[300] : colors.slate[500];
  const chevronColor = isDark ? colors.slate[500] : colors.slate[400];
  const hoverShadow = isDark ? '0 6px 16px rgba(0,0,0,0.4)' : '0 6px 16px rgba(15,23,42,0.06)';

  const inner = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 10,
        border: `1px solid ${cardBorder}`,
        background: cardBg,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'transform 100ms ease, border-color 100ms ease, box-shadow 100ms ease',
        opacity: disabled ? 0.55 : 1,
        height: '100%',
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.borderColor = accentColor;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = hoverShadow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = cardBorder;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: accentBg,
          color: accentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 17,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: titleColor,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {title}
          {note && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 4,
                background: noteBg,
                color: noteColor,
              }}
            >
              {note}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: descColor, marginTop: 2 }}>{desc}</div>
      </div>
      {!disabled && <RightOutlined style={{ fontSize: 11, color: chevronColor }} />}
    </div>
  );

  return disabled ? inner : <Link to={to} style={{ display: 'block' }}>{inner}</Link>;
});

const ActivityRow = observer(function ActivityRow({
  tone,
  text,
  detail,
  muted,
}: {
  tone: 'green' | 'ink' | 'violet' | 'amber';
  text: string;
  detail: string;
  muted?: boolean;
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '6px 0',
        opacity: muted ? 0.6 : 1,
      }}
    >
      <StatusPill tone={tone}>{tone === 'amber' ? 'Soon' : 'Done'}</StatusPill>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: isDark ? colors.slate[50] : colors.slate[900] }}>{text}</div>
        <div style={{ fontSize: 12, color: isDark ? colors.slate[400] : colors.slate[500], marginTop: 1 }}>{detail}</div>
      </div>
    </div>
  );
});

